import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { ApiService } from 'src/app/api.service';
import { FilterServiceService } from 'src/app/services/filter-service.service';

interface AppOption {
  code: string;
  name: string;
}

interface CityState {
  city: string;
  state: string;
}

@Component({
  selector: 'app-create-project',
  templateUrl: './create-project.component.html',
  styleUrls: ['./create-project.component.css'],
})
export class CreateProjectComponent implements OnInit {
  form: FormGroup;
  submitting = false;
  loadingMasters = true;

  readonly apps: AppOption[] = [
    { code: 'G001V001', name: 'SVAGRIHA' },
    { code: 'G002V001', name: 'SCHOOL GRIHA' },
    { code: 'G003V001', name: 'JAN GRIHA' },
    { code: 'G004V001', name: 'WATER GRIHA' },
    { code: 'G005V001', name: 'INTERIOR LITE' },
    { code: 'G006V001', name: 'INTERIOR DETAILED' },
    { code: 'G007V001', name: 'WASTE GRIHA' },
  ];

  readonly projectTypes = [
    'Residential',
    'Commercial',
    'Institutional',
    'Industrial',
    'Mixed Use',
    'Others',
  ];

  readonly statusOptions = [
    { value: 'Ongoing', label: 'Ongoing (Initiated)' },
    { value: 'REGISTERED', label: 'Registered' },
  ];

  clients: any[] = [];
  coordinators: any[] = [];
  auditors: any[] = [];
  cityStateList: CityState[] = [];
  states: string[] = [];
  filteredCities: CityState[] = [];
  filteredPiCities: CityState[] = [];

  constructor(
    private fb: FormBuilder,
    private filterService: FilterServiceService,
    private apiService: ApiService,
    private dialogRef: MatDialogRef<CreateProjectComponent>,
    private snackBar: MatSnackBar
  ) {
    this.form = this.fb.group({
      application_code: ['', Validators.required],
      project_code: ['', Validators.required],
      client_username: ['', Validators.required],
      coordinator_username: ['', Validators.required],
      auditor_username: [''],
      name: ['', Validators.required],
      address: [''],
      city: [''],
      state: [''],
      pin_code: [''],
      built_up_area: [''],
      description: [''],
      pi_owner: [''],
      pi_address: [''],
      pi_city: [''],
      pi_state: [''],
      pi_pin_code: [''],
      pi_email_id: [''],
      pi_fax: [''],
      pi_mobile: [''],
      pi_landline: [''],
      site_area: [''],
      pi_built_area: [''],
      ground_coverage: [''],
      s_training: [false],
      s_visit: [false],
      s_no_visit: [''],
      status_remark: [''],
      total_cost: [''],
      is_active: [true],
      is_on_hold: [false],
      type: [''],
      status: ['Ongoing', Validators.required],
      development_type: [''],
      total_no_of_non_teaching_staff: [''],
      total_no_of_teaching_staff: [''],
      total_number_of_students: [''],
      year_of_establishment: [''],
      school_timings_begin: [''],
      school_timings_end: [''],
      no_of_working_days: [''],
    });
  }

  ngOnInit(): void {
    this.loadMasters();
    this.form.get('application_code')?.valueChanges.subscribe((code) => {
      const auditorCtrl = this.form.get('auditor_username');
      if (code === 'G004V001') {
        auditorCtrl?.setValidators([Validators.required]);
      } else {
        auditorCtrl?.clearValidators();
      }
      auditorCtrl?.updateValueAndValidity();
    });
  }

  get isSchoolApp(): boolean {
    return this.form.get('application_code')?.value === 'G002V001';
  }

  get isWaterApp(): boolean {
    return this.form.get('application_code')?.value === 'G004V001';
  }

  loadMasters(): void {
    this.loadingMasters = true;
    let pending = 4;
    const done = () => {
      pending -= 1;
      if (pending <= 0) {
        this.loadingMasters = false;
      }
    };

    this.filterService.getClient().subscribe({
      next: (res: any) => {
        this.clients = res?.data || [];
        done();
      },
      error: () => done(),
    });

    this.filterService.getCoordinator().subscribe({
      next: (res: any) => {
        this.coordinators = res?.data || [];
        done();
      },
      error: () => done(),
    });

    this.filterService.getAuditor().subscribe({
      next: (res: any) => {
        this.auditors = res?.data || [];
        done();
      },
      error: () => done(),
    });

    this.filterService.getCity().subscribe({
      next: (res: any) => {
        this.cityStateList = (res?.data || []) as CityState[];
        this.states = Array.from(
          new Set(this.cityStateList.map((c) => c.state).filter(Boolean))
        ).sort((a, b) => a.localeCompare(b));
        this.filteredCities = [...this.cityStateList].sort((a, b) =>
          a.city.localeCompare(b.city)
        );
        this.filteredPiCities = [...this.filteredCities];
        done();
      },
      error: () => done(),
    });
  }

  onStateChange(): void {
    const state = this.form.get('state')?.value;
    this.filteredCities = this.cityStateList
      .filter((c) => !state || c.state === state)
      .sort((a, b) => a.city.localeCompare(b.city));
    const city = this.form.get('city')?.value;
    if (city && !this.filteredCities.some((c) => c.city === city)) {
      this.form.patchValue({ city: '' });
    }
  }

  onCityChange(): void {
    const city = this.form.get('city')?.value;
    const state = this.form.get('state')?.value;
    const match =
      this.cityStateList.find((c) => c.city === city && (!state || c.state === state)) ||
      this.cityStateList.find((c) => c.city === city);
    if (match?.state) {
      this.form.patchValue({ state: match.state }, { emitEvent: false });
      this.onStateChange();
    }
  }

  onPiStateChange(): void {
    const state = this.form.get('pi_state')?.value;
    this.filteredPiCities = this.cityStateList
      .filter((c) => !state || c.state === state)
      .sort((a, b) => a.city.localeCompare(b.city));
    const city = this.form.get('pi_city')?.value;
    if (city && !this.filteredPiCities.some((c) => c.city === city)) {
      this.form.patchValue({ pi_city: '' });
    }
  }

  onPiCityChange(): void {
    const city = this.form.get('pi_city')?.value;
    const state = this.form.get('pi_state')?.value;
    const match =
      this.cityStateList.find((c) => c.city === city && (!state || c.state === state)) ||
      this.cityStateList.find((c) => c.city === city);
    if (match?.state) {
      this.form.patchValue({ pi_state: match.state }, { emitEvent: false });
      this.onPiStateChange();
    }
  }

  cancel(): void {
    this.dialogRef.close(false);
  }

  submit(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      this.snackBar.open('Please fill all required fields.', 'Close', {
        duration: 3000,
      });
      return;
    }

    const v = this.form.value;
    const body: any = {
      app: { application_code: v.application_code },
      project_code: String(v.project_code).trim(),
      client: { username: v.client_username },
      coordinator: { username: v.coordinator_username },
      name: v.name?.trim(),
      address: v.address || undefined,
      city: v.city || undefined,
      state: v.state || undefined,
      pin_code: v.pin_code || undefined,
      built_up_area: this.toNumber(v.built_up_area),
      description: v.description || undefined,
      pi_owner: v.pi_owner || undefined,
      pi_address: v.pi_address || undefined,
      pi_city: v.pi_city || undefined,
      pi_state: v.pi_state || undefined,
      pi_pin_code: v.pi_pin_code || undefined,
      pi_email_id: v.pi_email_id || undefined,
      pi_fax: v.pi_fax || undefined,
      pi_mobile: v.pi_mobile || undefined,
      pi_landline: v.pi_landline || undefined,
      site_area: this.toNumber(v.site_area),
      pi_built_area: this.toNumber(v.pi_built_area),
      ground_coverage: this.toNumber(v.ground_coverage),
      s_training: !!v.s_training,
      s_visit: !!v.s_visit,
      s_no_visit: v.s_no_visit || undefined,
      status_remark: v.status_remark || undefined,
      total_cost: this.toNumber(v.total_cost),
      is_active: !!v.is_active,
      is_on_hold: !!v.is_on_hold,
      type: v.type || undefined,
      status: v.status,
      development_type: v.development_type || undefined,
    };

    if (v.auditor_username) {
      body.auditor = { username: v.auditor_username };
    }

    if (this.isSchoolApp) {
      body.NoOfNonTeachingStaff = v.total_no_of_non_teaching_staff || 0;
      body.NoOfTeachingStaff = v.total_no_of_teaching_staff || 0;
      body.NumberOfStudents = v.total_number_of_students || 0;
      body.YearOfEstablishment = v.year_of_establishment || undefined;
      body.SchoolTimingsBegin = v.school_timings_begin || undefined;
      body.SchoolTimingsEnd = v.school_timings_end || undefined;
      body.NoOfWorkingDays = v.no_of_working_days || undefined;
    }

    this.submitting = true;
    this.apiService.createProjectGrihaIntegration(body).subscribe({
      next: () => {
        this.submitting = false;
        this.snackBar.open('Project created successfully.', 'Close', {
          duration: 3000,
        });
        this.dialogRef.close(true);
      },
      error: (err) => {
        this.submitting = false;
        const message =
          err?.error?.message ||
          err?.error?.error ||
          err?.message ||
          'Failed to create project.';
        this.snackBar.open(message, 'Close', { duration: 5000 });
      },
    });
  }

  private toNumber(value: any): number | undefined {
    if (value === null || value === undefined || value === '') {
      return undefined;
    }
    const n = Number(value);
    return Number.isFinite(n) ? n : undefined;
  }
}
